/**
 * YouTube URL Utilities
 * 
 * Handles parsing and embedding YouTube videos using oEmbed API.
 * Supports:
 * - Full YouTube URLs: https://www.youtube.com/watch?v=VIDEO_ID
 * - Short URLs: https://youtu.be/VIDEO_ID
 * - Embed URLs: https://www.youtube.com/embed/VIDEO_ID
 * 
 * NO API KEY REQUIRED - uses YouTube's free oEmbed endpoint.
 */

/**
 * Extract video ID from various YouTube URL formats
 * @param {string} url - YouTube URL
 * @returns {string|null} Video ID or null if invalid
 */
export function extractYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  
  const trimmedUrl = url.trim();
  
  // Pattern 1: Standard watch URL - youtube.com/watch?v=VIDEO_ID
  const watchMatch = trimmedUrl.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.*&v=)([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return watchMatch[1];
  
  // Pattern 2: Short URL - youtu.be/VIDEO_ID
  const shortMatch = trimmedUrl.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (shortMatch) return shortMatch[1];
  
  // Pattern 3: Embed URL - youtube.com/embed/VIDEO_ID
  const embedMatch = trimmedUrl.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
  if (embedMatch) return embedMatch[1];
  
  // Pattern 4: Shorts URL - youtube.com/shorts/VIDEO_ID
  const shortsMatch = trimmedUrl.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (shortsMatch) return shortsMatch[1];
  
  // Pattern 5: Just the video ID (11 characters)
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmedUrl)) {
    return trimmedUrl;
  }
  
  return null;
}

/**
 * Validate if a URL is a valid YouTube URL
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
export function isValidYouTubeUrl(url) {
  return extractYouTubeVideoId(url) !== null;
}

/**
 * Generate embed URL from video ID
 * @param {string} videoId - YouTube video ID
 * @returns {string} Embed URL
 */
export function getYouTubeEmbedUrl(videoId) {
  if (!videoId) return '';
  return `https://www.youtube.com/embed/${videoId}`;
}

/**
 * Generate thumbnail URL from video ID
 * @param {string} videoId - YouTube video ID
 * @param {string} quality - Thumbnail quality: 'default', 'medium', 'high', 'maxres'
 * @returns {string} Thumbnail URL
 */
export function getYouTubeThumbnail(videoId, quality = 'high') {
  if (!videoId) return '';
  
  const qualityMap = {
    default: 'default',      // 120x90
    medium: 'mqdefault',     // 320x180
    high: 'hqdefault',       // 480x360
    maxres: 'maxresdefault'  // 1280x720 (may not exist for all videos)
  };
  
  const qualitySuffix = qualityMap[quality] || qualityMap.high;
  return `https://img.youtube.com/vi/${videoId}/${qualitySuffix}.jpg`;
}

/**
 * Fetch video metadata using YouTube oEmbed API (no API key required)
 * @param {string} url - YouTube URL
 * @returns {Promise<object|null>} Video metadata or null on error
 */
export async function fetchYouTubeMetadata(url) {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) {
    return { error: 'Invalid YouTube URL' };
  }
  
  try {
    // YouTube oEmbed endpoint - no API key needed
    const oEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
    
    const response = await fetch(oEmbedUrl);
    
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        return { error: 'Video is private or restricted' };
      }
      if (response.status === 404) {
        return { error: 'Video not found or has been deleted' };
      }
      return { error: 'Failed to fetch video information' };
    }
    
    const data = await response.json();
    
    return {
      success: true,
      videoId,
      title: data.title || 'Untitled Video',
      channelName: data.author_name || 'Unknown Channel',
      channelUrl: data.author_url || '',
      thumbnailUrl: getYouTubeThumbnail(videoId, 'high'),
      thumbnailMaxRes: getYouTubeThumbnail(videoId, 'maxres'),
      embedUrl: getYouTubeEmbedUrl(videoId),
      originalUrl: url,
      width: data.width,
      height: data.height,
      providerName: data.provider_name
    };
  } catch (error) {
    console.error('[YouTube] Fetch metadata error:', error);
    return { error: 'Network error - please check your connection' };
  }
}

/**
 * Normalize YouTube URL to standard format
 * @param {string} url - Any valid YouTube URL format
 * @returns {string} Normalized URL (youtube.com/watch?v=VIDEO_ID)
 */
export function normalizeYouTubeUrl(url) {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return url;
  return `https://www.youtube.com/watch?v=${videoId}`;
}

export default {
  extractYouTubeVideoId,
  isValidYouTubeUrl,
  getYouTubeEmbedUrl,
  getYouTubeThumbnail,
  fetchYouTubeMetadata,
  normalizeYouTubeUrl
};
